/** The only module that imports pdfjs-dist (on demand, so it is code-split). */
export interface RenderJob {
  /** Resolves when drawn, or when cancelled; rejects only on a genuine failure. */
  done: Promise<void>;
  cancel(): void;
}

export interface PdfDocument {
  numPages: number;
  renderPage(pageNumber: number, canvas: HTMLCanvasElement, cssWidth: number): RenderJob;
  destroy(): void;
}

interface RenderTask {
  promise: Promise<unknown>;
  cancel(): void;
}
interface RenderablePage {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: { canvas: HTMLCanvasElement; viewport: { width: number; height: number } }): RenderTask;
}
export interface PageSource {
  getPage(n: number): Promise<RenderablePage>;
}

const isCancel = (e: unknown) => (e as { name?: string } | null)?.name === "RenderingCancelledException";

/** Draws one page so its CSS width is `cssWidth`; cancelling stops it so the canvas can be reused safely. */
export function startPageRender(
  source: PageSource,
  n: number,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  dpr: number,
): RenderJob {
  let cancelled = false;
  let task: RenderTask | null = null;
  const done = (async () => {
    try {
      const page = await source.getPage(n);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const cssScale = cssWidth / base.width;
      const viewport = page.getViewport({ scale: cssScale * dpr });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(base.width * cssScale)}px`;
      canvas.style.height = `${Math.floor(base.height * cssScale)}px`;
      task = page.render({ canvas, viewport });
      await task.promise;
    } catch (e) {
      if (cancelled || isCancel(e)) return;
      throw e;
    }
  })();
  return {
    done,
    cancel() {
      cancelled = true;
      task?.cancel();
    },
  };
}

let workerConfigured = false;

export async function loadPdf(data: ArrayBuffer): Promise<PdfDocument> {
  const pdfjs = await import("pdfjs-dist");
  if (!workerConfigured) {
    workerConfigured = true;
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString();
  }
  // pdf.js transfers the buffer to its worker: hand it a copy so the cached bytes stay usable.
  const task = pdfjs.getDocument({
    data: new Uint8Array(data.slice(0)),
    disableAutoFetch: true,
  });
  const doc = await task.promise;
  return {
    numPages: doc.numPages,
    renderPage(n, canvas, cssWidth) {
      return startPageRender(doc as unknown as PageSource, n, canvas, cssWidth, window.devicePixelRatio || 1);
    },
    destroy() {
      // In pdf.js 6 only the loading task is destroyable.
      void task.destroy();
    },
  };
}
